import React, { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import getColor from "./utils/getColor";
import { convertFromUnderscoreCase } from "./utils/toSentenceCase";
import "./styles.css";

const ProcessPrediction = ({
  digibasicResponse,
  windowWidth,
  setSelectedOption,
}) => {
  const [parametersToPlot, setParametersToPlot] = useState(["Feed_Force"]);
  const [lastSelectedParameter, setLastSelectedParameter] =
    useState("Feed_Force");

  const PARAMETER_TYPES = [
    "Feed_Force",
    "CrossFeed_Force",
    "Axial_Force",
    "Side_Force",
    "Torque",
    "Power",
    "ChipThickness",
  ];

  const cardStyle = {
    background: "#ffffff",
    border: "1px solid #e6e9ec",
    borderRadius: "14px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  };

  const computeChartData = () => {
    const allParameterData = [];

    const chipThicknessKeys = Object.keys(
      digibasicResponse?.Original?.ChipThickness || {}
    );

    const chipThicknessFirstKey = chipThicknessKeys[0];
    const chipThicknessFirstValue =
      digibasicResponse?.Original?.ChipThickness?.[chipThicknessFirstKey] || [];

    const chipThicknessLength = chipThicknessFirstValue.length;

    parametersToPlot
      .filter((item) => !item.startsWith("ChipThickness_"))
      .forEach((parameterType) => {
        const chartDataFilteredByParameterType =
          digibasicResponse?.Original?.[parameterType];

        if (!chartDataFilteredByParameterType) {
          return;
        }

        const parameterData = Object.entries(chartDataFilteredByParameterType);

        if (parameterType === "ChipThickness") {
          const chipThicknessData = [];

          parameterData.forEach(([degree, parameterValue]) => {
            if (!Array.isArray(parameterValue)) {
              return;
            }

            for (let i = 0; i < chipThicknessLength; i++) {
              chipThicknessData.push({
                degree: parseFloat(degree),
                [`ChipThickness_${i + 1}`]: parameterValue[i],
              });
            }
          });

          const groupedItems = Object.entries(
            chipThicknessData.reduce((acc, curr) => {
              Object.keys(curr).forEach((key) => {
                if (key.startsWith("ChipThickness_")) {
                  const index = key.split("_")[1];
                  if (!acc[index]) {
                    acc[index] = [];
                  }
                  acc[index].push(curr);
                }
              });
              return acc;
            }, {})
          ).map(([, value]) =>
            value.sort((a, b) => {
              return a.degree - b.degree;
            })
          );

          allParameterData.push(...groupedItems);
        } else {
          const formattedParameterData = parameterData
            .map(([degree, parameterValue]) => ({
              degree: parseFloat(degree),
              [parameterType]: parameterValue,
            }))
            .sort((a, b) => a.degree - b.degree);

          allParameterData.push(formattedParameterData);
        }
      });

    const parameterLength = allParameterData[0]?.length || 0;

    const chartDataPoints = [];
    for (let i = 0; i < parameterLength; i++) {
      let finalChartDataPoint = {};
      allParameterData.forEach((parameterValues) => {
        finalChartDataPoint = { ...finalChartDataPoint, ...parameterValues[i] };
      });
      chartDataPoints.push(finalChartDataPoint);
    }

    return chartDataPoints.map((dataPoint) => {
      const { ChipThickness, ...rest } = dataPoint;
      if (!ChipThickness) return rest;

      const newProps = ChipThickness.reduce(
        (acc, val, i) => ({ ...acc, [`ChipThickness_${i + 1}`]: val }),
        {}
      );

      return { ...rest, ...newProps };
    });
  };

  const getAveMaxValues = (data, selectedParameter) => {
    if (!selectedParameter) {
      return {
        ave: "-",
        max: "-",
        unit: "",
      };
    }

    const aveKey = `Ave_${selectedParameter}`;
    const maxKey = `Max_${selectedParameter}`;

    if (selectedParameter === "ChipThickness") {
      return {
        ave: data?.[aveKey] ?? "-",
        max: data?.[maxKey] ?? "-",
        unit: "mm",
      };
    }

    return {
      ave: data?.[aveKey] ?? "-",
      max: data?.[maxKey] ?? "-",
      unit:
        selectedParameter === "Torque"
          ? "Nm"
          : selectedParameter === "Power"
          ? "W"
          : "N",
    };
  };

  const formatMetric = (value) => {
    if (value === "-" || value == null || Number.isNaN(Number(value))) {
      return "-";
    }
    return Number(value).toFixed(1);
  };

  const getYAxisLabel = () => {
    if (!parametersToPlot.length) return undefined;

    if (parametersToPlot.length === 1) {
      if (parametersToPlot[0] === "Power") return "Power (W)";
      if (parametersToPlot[0] === "Torque") return "Torque (Nm)";
      if (parametersToPlot[0] === "ChipThickness") return "Chip Thickness (mm)";
      return "Force (N)";
    }

    if (parametersToPlot.includes("ChipThickness")) {
      return "Chip Thickness (mm)";
    }

    return "Force (N)";
  };

  const handleParameterClick = (parameterType) => {
    const doesParameterExist = parametersToPlot.some(
      (parameter) => parameter === parameterType
    );

    if (doesParameterExist) {
      const selectedBaseParameters = parametersToPlot.filter(
        (item) => !item.startsWith("ChipThickness_")
      );

      if (
        selectedBaseParameters.length === 1 &&
        selectedBaseParameters[0] === parameterType
      ) {
        return;
      }

      const newParametersToPlot = parametersToPlot.filter(
        (parameter) => parameter !== parameterType
      );

      if (parameterType === "ChipThickness") {
        setParametersToPlot(
          newParametersToPlot.filter(
            (item) => !item.startsWith("ChipThickness_")
          )
        );
      } else {
        setParametersToPlot(newParametersToPlot);
      }

      if (lastSelectedParameter === parameterType) {
        const remainingBaseParameters = newParametersToPlot.filter(
          (item) => !item.startsWith("ChipThickness_")
        );
        setLastSelectedParameter(
          remainingBaseParameters[remainingBaseParameters.length - 1] ||
            "Feed_Force"
        );
      }

      return;
    }

    const chipThickness = digibasicResponse?.Original?.ChipThickness || {};
    const chipThicknessKeys = Object.keys(chipThickness);
    const chipThicknessLength =
      chipThicknessKeys.length > 0 ? chipThickness[chipThicknessKeys[0]].length : 0;

    const chipThicknessArray = [];
    for (let i = 0; i < chipThicknessLength; i++) {
      chipThicknessArray.push(`ChipThickness_${i + 1}`);
    }

    if (parameterType === "ChipThickness") {
      setParametersToPlot([parameterType, ...chipThicknessArray]);
    } else if (parameterType === "Torque" || parameterType === "Power") {
      setParametersToPlot([parameterType]);
    } else {
      setParametersToPlot(
        [
          ...parametersToPlot.filter(
            (parameter) => !parameter.includes("ChipThickness")
          ),
          parameterType,
        ].filter(
          (item) =>
            item !== "ChipThickness" && item !== "Torque" && item !== "Power"
        )
      );
    }

    setLastSelectedParameter(parameterType);
  };

  if (digibasicResponse === undefined) {
    return <h1>Loading...</h1>;
  }

  const chartDataToBeDisplayed = computeChartData();
  const selectedMetric = getAveMaxValues(
    digibasicResponse.Original,
    lastSelectedParameter
  );

  const metricCards = [
    {
      title: "MRR",
      value: formatMetric(digibasicResponse?.Original?.MRR),
      unit: "cm³/min",
    },
    {
      title: "Max",
      value: formatMetric(selectedMetric.max),
      unit: selectedMetric.unit,
      subtitle: convertFromUnderscoreCase(lastSelectedParameter),
    },
    {
      title: "Average",
      value: formatMetric(selectedMetric.ave),
      unit: selectedMetric.unit,
      subtitle: convertFromUnderscoreCase(lastSelectedParameter),
    },
  ];
  
  return (
  <div className="process-prediction-page">
    <h2 className="d-flex justify-content-center mb-4">Process Prediction</h2>

    <div className="process-prediction-layout">
      <div className="pp-parameters">
        <div style={{ ...cardStyle, overflow: "hidden", height: "100%" }}>
          {PARAMETER_TYPES.map((parameterType, index) => {
            const isActive = parametersToPlot.some(
              (parameter) => parameter === parameterType
            );

            return (
              <button
                key={parameterType}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleParameterClick(parameterType);
                }}
                style={{
                  width: "100%",
                  minHeight: "64px",
                  color: "#1f2d3d",
                  fontWeight: isActive ? 600 : 500,
                  backgroundColor: isActive ? "#bcc7d1" : "#eef2f5",
                  border: "none",
                  borderBottom:
                    index !== PARAMETER_TYPES.length - 1
                      ? "1px solid #dbe2e8"
                      : "none",
                  padding: "14px 18px",
                  textAlign: "center",
                  transition: "background-color 0.2s ease",
                  cursor: "pointer",
                }}
              >
                {convertFromUnderscoreCase(parameterType)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="pp-chart">
        <div style={{ ...cardStyle, padding: "12px", height: "100%" }}>
          <div style={{ width: "100%", height: "100%", minHeight: "260px" }}>
            <ResponsiveContainer>
              <LineChart
                data={chartDataToBeDisplayed}
                margin={{ top: 30, right: 15, left: 25, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="degree"
                  tickCount={10}
                  label={{
                    value: "Rotation Angle (deg)",
                    position: "insideBottomRight",
                    offset: -12,
                  }}
                />
                <YAxis
                  label={{
                    value: getYAxisLabel(),
                    angle: 0,
                    position: "insideTopLeft",
                    dx: -10,
                    dy: -20,
                  }}
                />
                <Tooltip />
                <Legend />
                {parametersToPlot
                  .filter((item) => item !== "ChipThickness")
                  .map((parameterType) => (
                    <Line
                      key={parameterType}
                      type="monotone"
                      dataKey={parameterType}
                      stroke={getColor(parameterType)}
                      dot={false}
                      strokeWidth={2}
                    />
                  ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="pp-image">
        <div
          style={{
            ...cardStyle,
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "10px",
          }}
        >
          <img
            style={{
              width: "100%",
              maxWidth: "100%",
              maxHeight: "180px",
              objectFit: "contain",
              borderRadius: "8px",
            }}
            src="assets/images/forces.png"
            alt="Force directions"
          />
        </div>
      </div>

      <div className="pp-metric-1">
        <div style={{ ...cardStyle, padding: "12px 14px", height: "100%" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: 700,
              color: "#6c757d",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {metricCards[0].title}
          </div>
          <div
            style={{
              fontSize: "26px",
              fontWeight: 700,
              color: "#1f2d3d",
              lineHeight: 1.2,
              marginTop: "8px",
            }}
          >
            {metricCards[0].value}
          </div>
          <div style={{ fontSize: "15px", color: "#6c757d", marginTop: "6px" }}>
            {metricCards[0].unit}
          </div>
        </div>
      </div>

      <div className="pp-metric-2">
        <div style={{ ...cardStyle, padding: "12px 14px", height: "100%" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: 700,
              color: "#6c757d",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {metricCards[1].title}
          </div>
          <div
            style={{
              fontSize: "26px",
              fontWeight: 700,
              color: "#1f2d3d",
              lineHeight: 1.2,
              marginTop: "8px",
            }}
          >
            {metricCards[1].value}
          </div>
          <div style={{ fontSize: "15px", color: "#6c757d", marginTop: "6px" }}>
            {metricCards[1].unit}
          </div>
          <div style={{ fontSize: "13px", color: "#98a2ad", marginTop: "8px" }}>
            {metricCards[1].subtitle}
          </div>
        </div>
      </div>

      <div className="pp-metric-3">
        <div style={{ ...cardStyle, padding: "12px 14px", height: "100%" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: 700,
              color: "#6c757d",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {metricCards[2].title}
          </div>
          <div
            style={{
              fontSize: "26px",
              fontWeight: 700,
              color: "#1f2d3d",
              lineHeight: 1.2,
              marginTop: "8px",
            }}
          >
            {metricCards[2].value}
          </div>
          <div style={{ fontSize: "15px", color: "#6c757d", marginTop: "6px" }}>
            {metricCards[2].unit}
          </div>
          <div style={{ fontSize: "13px", color: "#98a2ad", marginTop: "8px" }}>
            {metricCards[2].subtitle}
          </div>
        </div>
      </div>

      <div className="pp-next">
        <button
          className="btn waves-effect waves-light"
          style={{
            backgroundColor: "#3498DB",
            color: "#fff",
            borderRadius: "8px",
            fontWeight: 600,
            height: "100%",
            minHeight: "44px",
            padding: "0 18px",
          }}
          onClick={() => {
            setSelectedOption("feedRateOptimization");
          }}
        >
          Next
        </button>
      </div>
    </div>
  </div>
);
};

export default ProcessPrediction;